import { Box, Menu, MenuItem, Stack } from '@mui/material'
import { Permissions } from 'c1gen/permissions'
import { C1Button, C1ButtonProps, C1IconButton } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { usePermissionChecker } from 'components/no-ui/capability'
import { ChevronDown, MoreHorizontal } from 'lucide-react'
import React, { MouseEvent, useId, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

/**
 * Represents an option for the C1MoreButton component.
 */
export type C1MoreButtonOptionProps = {
    key?: string,
    /** The permission required to show this item */
    permission: Permissions,
    label: React.ReactNode,
    onClick: () => void,
    disabled?: boolean,
    icon?: React.ReactElement,
}

type C1MoreButtonProps = {
    /** The options to show in the dropdown menu */
    options: C1MoreButtonOptionProps[]
    /** Optional label to display on the button. When provided, renders as a labeled button instead of an icon button */
    label?: React.ReactNode
    /** Defaults to 'outlined' */
    variant?: C1ButtonProps['variant']
    /** Optional test ID for the button */
    'data-testid'?: string
}

/**
 * A button component that displays a menu with multiple options when clicked.
 * Renders as an icon button (default) or labeled button (when label prop is provided) with a dropdown menu.
 *
 * @param {C1MoreButtonProps} props - The properties for the C1MoreButton component.
 * @returns {JSX.Element} The rendered C1MoreButton component.
 */
export const C1MoreButton: React.FC<C1MoreButtonProps> = ({
    options,
    label,
    variant = 'outlined',
    'data-testid': dataTestId,
}) => {
    const intl = useIntl()
    const id = useId()
    const buttonId = `c1-more-button-${id}`
    const menuId = `c1-more-menu-${id}`

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const hasPermission = usePermissionChecker()
    const menuRef = useRef<HTMLDivElement>(null)

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = (): void => {
        setAnchorEl(null)
    }

    // Filter options based on permissions before rendering
    const visibleOptions = options.filter((o) => o && hasPermission(o.permission))

    if (visibleOptions.length === 0) {
        return null
    }

    return <>
        {label
            ? <Box>
                <C1Button
                    id={buttonId}
                    data-testid={dataTestId}
                    variant={variant}
                    aria-controls={open ? menuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={open}
                    onClick={handleClick}
                    endIcon={<C1Icon icon={ChevronDown} />}
                >
                    {label}
                </C1Button>
            </Box>
            : <C1IconButton
                id={buttonId}
                data-testid={dataTestId}
                aria-controls={open ? menuId : undefined}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={intl.formatMessage({ id: 'component_more_button_label', defaultMessage: 'More' })}
                onClick={handleClick}
                {...(variant === 'text'
                    ? { variant: 'text' as const }
                    : { variant: 'outlined' as const, color: 'info' })}
            >
                <C1Icon icon={MoreHorizontal} />
            </C1IconButton>
        }
        <Menu
            ref={menuRef}
            id={menuId}
            aria-label={intl.formatMessage({ id: 'component_more_button_menu', defaultMessage: 'Actions menu' })}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': buttonId,
                autoFocusItem: true,
                variant: 'menu',
            }}
            TransitionProps={{
                onEntered: (): void => {
                    // Focus first menu item after transition completes
                    // Use ref to scope query to this specific menu instance
                    if (menuRef.current) {
                        setTimeout(() => {
                            const firstMenuItem = menuRef.current?.querySelector('[role="menuitem"]') as HTMLElement
                            firstMenuItem?.focus()
                        }, 100)
                    }
                },
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            {visibleOptions.map((o, i) => (
                <MenuItem
                    disabled={o.disabled}
                    key={o.key || `${i}`}
                    onClick={(): void => {
                        handleClose()
                        o.onClick()
                    }}
                >
                    {o.icon ? <Stack direction="row" alignItems="center" spacing={1}>
                        {o.icon}
                        <Box>{o.label}</Box>
                    </Stack> : o.label}
                </MenuItem>
            ))}
        </Menu>
    </>
}
